'use client';

import {
  PatchTelegramUsersDto,
  TelegramUserMeResponseDto,
  useTelegramUsersGetMe,
  useTelegramUsersPatch,
} from '@/generated';
import { usePathname } from '@/navigation';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useModals } from './ModalContext';
import { clearClientAuthCookies } from '@/utils/auth-cookies';
import { useTelegramMiniApp } from './TelegramMiniAppContext';

const LoginPageContextProvider = dynamic(
  () => import('@/app/[lang]/(main)/login/components/LoginPageContext').then((mod) => mod.LoginPageContextProvider),
  { ssr: false },
);
const LoginModal = dynamic(
  () => import('@/app/[lang]/(main)/login/components/LoginModal').then((mod) => mod.LoginModal),
  { ssr: false },
);

const defaultNoop = (_?: string) => {};

type LoginContext = {
  isLoading: boolean;
  isLogined: boolean;
  userData?: TelegramUserMeResponseDto;
  updateUser?: (newUser: PatchTelegramUsersDto) => void;
  logout: () => void;
  openLoginModal: (onCompleteLogin?: () => void, authTextType?: string) => void;
  closeLoginModal: () => void;
  isLoginModalOpened: boolean;
  setIsLoginModalOpened: (state: boolean, onCompleteLogin?: () => void, authTextType?: string) => void;
};

export const LoginContext = createContext<LoginContext>({
  isLogined: false,
  isLoading: true,
  logout: defaultNoop,
  openLoginModal: () => {},
  closeLoginModal: defaultNoop,
  isLoginModalOpened: false,
  setIsLoginModalOpened: () => {},
});

export const LoginContextProvider = ({ children }) => {
  const pathname = usePathname();
  const [onCompleteLogin, setOnCompleteLogin] = useState<(() => void) | null>(null);
  const [loginAuthText, setLoginAuthText] = useState<string>();
  const { isMiniAppMode } = useTelegramMiniApp();
  const queryClient = useQueryClient();

  const { data: me, isLoading } = useTelegramUsersGetMe({
    query: {
      queryKey: ['get-me-once-unique'],
      staleTime: Infinity,
    },
    client: {
      handle401: false,
    },
  });

  const modalKey = 'login-modal';
  const { open: openModal, close: closeModal, modals } = useModals();
  const isLoginModalOpened = !!modals.find((m) => m.key === modalKey);
  const scrollPositionRef = useRef(0);
  const onCompleteLoginRef = useRef(onCompleteLogin);
  const loginAuthTextRef = useRef(loginAuthText);
  onCompleteLoginRef.current = onCompleteLogin;
  loginAuthTextRef.current = loginAuthText;

  const setIsLoginModalOpened = useCallback(
    (state: boolean, onCompleteCb?: () => void, authText?: string) => {
      if (isMiniAppMode) {
        if (!state) {
          closeModal(modalKey);
        }
        return;
      }

      if (state) {
        openModal(
          modalKey,
          <LoginPageContextProvider
            onCloseModal={onCompleteCb || onCompleteLoginRef.current || undefined}
            authTextType={authText || loginAuthTextRef.current}
          >
            <LoginModal />
          </LoginPageContextProvider>,
          true,
          undefined,
          'md:bg-black/30 bg-white opacity-100',
          true,
          true,
        );
        return;
      }

      const url = new URL(window.location.href);
      if (url.searchParams.has('login') || url.searchParams.has('redirect')) {
        url.searchParams.delete('login');
        url.searchParams.delete('redirect');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      closeModal(modalKey);
    },
    [isMiniAppMode, openModal, closeModal, modalKey],
  );

  const setIsLoginModalOpenedRef = useRef(setIsLoginModalOpened);
  setIsLoginModalOpenedRef.current = setIsLoginModalOpened;

  useEffect(() => {
    const read = () => {
      if (isMiniAppMode) {
        closeModal(modalKey);
        return;
      }

      const url = new URL(window.location.href);
      const shouldOpen = url.searchParams.get('login') === 'true' && !me;

      setIsLoginModalOpenedRef.current(shouldOpen);
    };

    // initial
    read();

    // react to back/forward
    const onPopState = () => read();
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [closeModal, isMiniAppMode, me, modalKey, pathname]);

  const { mutateAsync: updateUser } = useTelegramUsersPatch({
    client: {
      handle401: false,
    },
  });

  useEffect(() => {
    if (!isLoginModalOpened) {
      return;
    }

    const content = document.getElementById('content');
    const bodyStyle = document.body.style;

    scrollPositionRef.current = window.scrollY;

    if (content) {
      content.style.overflowY = 'hidden';
    }

    const previousBodyStyles = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      width: bodyStyle.width,
      overflowY: bodyStyle.overflowY,
    };

    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollPositionRef.current}px`;
    bodyStyle.width = '100%';
    bodyStyle.overflowY = 'hidden';

    return () => {
      if (content) {
        content.style.overflowY = 'auto';
      }

      bodyStyle.position = previousBodyStyles.position;
      bodyStyle.top = previousBodyStyles.top;
      bodyStyle.width = previousBodyStyles.width;
      bodyStyle.overflowY = previousBodyStyles.overflowY;

      window.scrollTo({ top: scrollPositionRef.current });
    };
  }, [isLoginModalOpened]);

  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpened(false);
  }, [setIsLoginModalOpened]);

  const openLoginModal = useCallback(
    (onCompleteLoginCb?: () => void, authTextType?: string) => {
      if (isMiniAppMode) {
        return;
      }
      setLoginAuthText(authTextType || '');
      if (onCompleteLoginCb) {
        setOnCompleteLogin(() => onCompleteLoginCb);
      }
      setIsLoginModalOpened(true, onCompleteLoginCb, authTextType);
    },
    [isMiniAppMode, setIsLoginModalOpened],
  );

  const logout = useCallback(() => {
    clearClientAuthCookies();
    queryClient.setQueryData(['get-me-once-unique'], null);
    if (isMiniAppMode) {
      window.location.reload();
    }
  }, [isMiniAppMode, queryClient]);

  const update = useCallback(
    async (newUser: PatchTelegramUsersDto) => {
      await updateUser({ data: newUser });
    },
    [updateUser],
  );

  const value = {
    updateUser: update,
    userData: me,
    isLogined: !!me,
    isLoading,
    logout,
    closeLoginModal,
    openLoginModal,
    isLoginModalOpened,
    setIsLoginModalOpened,
  };

  return (
    <>
      <LoginContext.Provider value={value}>{children}</LoginContext.Provider>
    </>
  );
};

export const useIsLogined = () => useContextSelector(LoginContext, (value) => value.isLogined);
export const useUserData = () => useContextSelector(LoginContext, (value) => value.userData);
export const useLogout = () => useContextSelector(LoginContext, (value) => value.logout);
export const useOpenLoginModal = () => useContextSelector(LoginContext, (value) => value.openLoginModal);
export const useCloseLoginModal = () => useContextSelector(LoginContext, (value) => value.closeLoginModal);
export const useIsLoading = () => useContextSelector(LoginContext, (value) => value.isLoading);
